import type {
  DeleteQuestionnaireArgs,
  FetchQuestionnaireResultsArgs,
  FetchQuestionnaireByIdArgs,
  ArchiveQuestionnaireArgs,
  UnarchiveQuestionnaireArgs,
  CreateQuestionnaireArgs,
  UpdateQuestionnaireArgs,
  FetchQuestionnairesArgs,
  CopyQuestionnaireArgs,
} from './types/questionnaires.types';
import type { ApiQuestionnaireResults } from '../schemas/questionnaireResults.schema';
import type { ApiQuestionnaire } from '../schemas/questionnaires.schema';
import type { BaseApiResponse } from './types/api.types';

import {
  mapQuestionnaireFromApi,
  mapQuestionnaireToApi,
} from './mappers/questionnaires.mappers';
import { mapQuestionnaireWithQuestionsFromApi } from './mappers/questionnaireSubmissions.mappers';
import { mapQuestionnaireResultsFromApi } from './mappers/questionnaireResults.mappers';
import { destroy, patch, post, get } from './axios';

const baseUrl = (organizationId: string) =>
  `/v2/organizations/${organizationId}/questionnaires`;

export async function fetchQuestionnaires({
  organizationId,
  status,
}: FetchQuestionnairesArgs) {
  const response = await get<BaseApiResponse<ApiQuestionnaire[]>>(
    baseUrl(organizationId),
    { query: { status } },
  );
  if (!response) return null;
  return response.data.map(mapQuestionnaireFromApi);
}

export async function fetchQuestionnaireById({
  id,
  organizationId,
}: FetchQuestionnaireByIdArgs) {
  const response = await get<BaseApiResponse<ApiQuestionnaire>>(
    `${baseUrl(organizationId)}/${id}`,
  );
  // DECIMAL columns arrive as strings; the builder resets its form from this
  // payload directly, so "1.000" would show up in Min/Max and break the
  // Decimals select's value match.
  return response?.data
    ? mapQuestionnaireWithQuestionsFromApi(response.data)
    : null;
}

export async function fetchQuestionnaireResults({
  id,
  organizationId,
}: FetchQuestionnaireResultsArgs) {
  const response = await get<BaseApiResponse<ApiQuestionnaireResults>>(
    `${baseUrl(organizationId)}/${id}/results`,
  );
  if (!response) return null;
  return mapQuestionnaireResultsFromApi(response.data);
}

export async function createQuestionnaire({
  data,
  organizationId,
}: CreateQuestionnaireArgs) {
  const response = await post<ApiQuestionnaire>(
    baseUrl(organizationId),
    mapQuestionnaireToApi(data),
  );
  return response?.data ?? null;
}

export async function updateQuestionnaire({
  id,
  data,
  organizationId,
  includeQuestions,
}: UpdateQuestionnaireArgs) {
  const response = await patch<ApiQuestionnaire>(
    `${baseUrl(organizationId)}/${id}`,
    mapQuestionnaireToApi(data, { includeQuestions }),
  );
  return response?.data ?? null;
}

export async function archiveQuestionnaire({
  id,
  organizationId,
}: ArchiveQuestionnaireArgs) {
  const response = await post<ApiQuestionnaire>(
    `${baseUrl(organizationId)}/${id}/archive`,
    {},
  );
  return response?.data ?? null;
}

export async function unarchiveQuestionnaire({
  id,
  organizationId,
}: UnarchiveQuestionnaireArgs) {
  const response = await post<ApiQuestionnaire>(
    `${baseUrl(organizationId)}/${id}/unarchive`,
    {},
  );
  return response?.data ?? null;
}

export async function copyQuestionnaire({
  id,
  organizationId,
}: CopyQuestionnaireArgs) {
  const response = await post<ApiQuestionnaire>(
    `${baseUrl(organizationId)}/${id}/copy`,
    {},
  );
  return response?.data ?? null;
}

export async function deleteQuestionnaire({
  id,
  organizationId,
}: DeleteQuestionnaireArgs) {
  await destroy(`${baseUrl(organizationId)}/${id}`);
}
